'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import { warmGrey } from '@enlighten/design-system/dist/colors';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React from 'react';

import type { NoticePopupImage } from '@/components/layouts/NoticePopup/types';

interface Props {
	image: NoticePopupImage;
	paths: string[];
	url?: string;
}

/**
 * 젠데스크 런처 실측값 (우하단 16px, 64×64, 모바일 동일)
 *
 * - 위젯 위치 설정 변경 시 함께 수정
 */
const ZENDESK_LAUNCHER_OFFSET = 16;
const ZENDESK_LAUNCHER_SIZE = 64;
const GAP = 20;

const buttonSx = { height: 32, padding: '6px 8px', borderRadius: 0, fontSize: '12px !important', fontWeight: 600, lineHeight: 1.4, color: '#36383A' };

const Banner: React.FC<Props> = ({ image, paths, url }) => {
	const pathname = usePathname();
	const [closedPath, setClosedPath] = React.useState<string | null>(null);

	const isExternalUrl = Boolean(url?.startsWith('http'));
	// WP 본문에 슬래시 없는 내부 경로(`notice`) 존재 → 앞에 `/` 보정
	const href = url && (isExternalUrl || url.startsWith('/') ? url : `/${url}`);
	const linkProps = { target: isExternalUrl ? '_blank' : undefined, rel: isExternalUrl ? 'noopener noreferrer' : undefined };

	const handleCloseClick = () => {
		setClosedPath(pathname);
	};

	// layout 상주라 페이지 이동 후에도 닫힘 유지 → 경로 바뀌면 초기화
	if (closedPath && closedPath !== pathname) {
		setClosedPath(null);
	}

	if (!paths.includes(pathname) || closedPath === pathname) {
		return null;
	}

	return (
		<Box
			position="fixed"
			right={ZENDESK_LAUNCHER_OFFSET}
			bottom={ZENDESK_LAUNCHER_OFFSET + ZENDESK_LAUNCHER_SIZE + GAP}
			/** 젠데스크 런처와 같은 층 (공지 팝업 9999999 아래) */
			zIndex={999999}
			width={102}
			overflow="hidden"
			borderRadius="10px"
			boxShadow="0 4px 10px rgba(0, 0, 0, 0.05)"
		>
			<Box {...linkProps} component={href ? Link : 'div'} href={href} aria-label={image.alt} display="block">
				<Image src={image.url} alt="" width={102} height={124} style={{ display: 'block', objectFit: 'cover' }} />
			</Box>

			<Box display="flex" bgcolor="common.white">
				{href ? (
					<Button {...linkProps} component={Link} href={href} variant="text" sx={{ ...buttonSx, flex: 1, minWidth: 0, borderRight: `1px solid ${warmGrey[300]}` }}>
						신청하기
					</Button>
				) : null}
				<Button variant="text" sx={{ ...buttonSx, width: href ? 40 : '100%', minWidth: 0 }} onClick={handleCloseClick}>
					닫기
				</Button>
			</Box>
		</Box>
	);
};

export default Banner;
